import {
  EventEntity,
  EventParticipantEntity,
  ThemeEntity,
  UserEntity,
} from "@citychallenge/api-public";
import { Loadable } from "../loadable";
import {
  IsNotEmpty,
  IsDateString,
  IsOptional,
  IsNumber,
  IsString,
  validateSync,
  validateOrReject,
  IsBoolean,
  IsUrl,
} from "class-validator";
import { IsValidMarkdown } from "./validators";
import { Types } from "@citychallenge/common";
import dayjs from "dayjs";
import { computed, observable, action } from "mobx";
import { LocalizedMoment } from "../i18n";
import { MarkdownParser } from "../markdown";

interface ObservableUserStore {
  user?: UserEntity;
}

export class EventParticipantModel {
  public user!: UserEntity;
  public created!: string;

  public confirmed?: string;

  public demioJoinUrl?: string;

  public webinarGeekJoinUrl?: string;

  constructor(
    data: EventParticipantEntity,
    private userStore: ObservableUserStore
  ) {
    Object.assign(this, data);
  }

  @computed
  public get isCurrentUser() {
    return this.user.id === this.userStore.user?.id;
  }
}

export class EventModel
  implements Omit<EventModel, "participants" | "division">
{
  public static create(
    defaults: Partial<EventEntity> = {}
  ): Partial<EventModel> {
    return new EventModel(defaults as EventEntity, {});
  }
  public id!: string;

  @IsNotEmpty()
  @observable
  public name!: string;

  @IsDateString()
  @observable
  public startDate!: string;

  @IsNumber()
  @IsOptional()
  @observable
  public maxParticipants!: number;

  @IsDateString()
  @IsOptional()
  @observable
  public endDate?: string;

  @IsValidMarkdown()
  @observable
  public description?: Types["MarkdownText"];

  @IsString()
  @IsOptional()
  @observable
  public phone?: string;

  @IsNumber()
  @IsOptional()
  @observable
  public energyPoints?: number;

  @IsString()
  @IsOptional()
  @observable
  public location?: string;

  @IsOptional()
  @IsBoolean()
  @observable
  public hideParticipants?: boolean;

  @IsOptional()
  @IsUrl()
  @observable
  public link?: string;

  @IsNotEmpty()
  @IsOptional()
  @observable
  public image?: string;

  @IsOptional()
  @IsBoolean()
  @observable
  public eventIsVisibleToUser?: string;

  @observable
  public theme?: ThemeEntity;

  public user?: UserEntity;

  public demioEventId?: string;

  public webinarGeekBroadcastId?: string;

  @computed
  public get isValid() {
    const errors = validateSync(this);
    return !errors.length;
  }

  public async getValidatedObject() {
    await validateOrReject(this);

    return {
      name: this.name,
      image: this.image,
      startDate: this.startDate,
      maxParticipants: this.maxParticipants,
      description: this.description,
      phone: this.phone,
      location: this.location,
      energyPoints: this.energyPoints,
      hideParticipants: this.hideParticipants,
    };
  }

  public participantsLoadable?: Loadable<EventParticipantModel[]>;

  @observable
  public participants: EventParticipantModel[] = [];

  @action
  public setParticipants(participants: EventParticipantEntity[]) {
    this.participants = participants.map((data) => {
      return new EventParticipantModel(data, this.userStore);
    });
  }

  @computed
  public get isOnWaitingList() {
    return !!this.waitingParticipants.find((p) => p.isCurrentUser);
  }

  @computed
  public get roomLeft() {
    if (!this.maxParticipants) return true;

    return this.confirmedParticipants.length < this.maxParticipants;
  }

  @computed
  public get confirmedParticipants() {
    return this.participants.filter((p) => !!p.confirmed && !!p.user);
  }

  @computed
  public get waitingParticipants() {
    return this.participants.filter((p) => !p.confirmed && !!p.user);
  }

  @computed
  public get isParticipating() {
    return !!this.participants.find((p) => p.isCurrentUser);
  }

  constructor(data: EventEntity, private userStore: ObservableUserStore) {
    Object.assign(this, data);
  }

  public runningInHotlink?: boolean;

  @computed
  public get startDateLocalized() {
    if (!this.startDate) return undefined;
    return new LocalizedMoment(this.startDate);
  }

  @computed
  public get endDateLocalized() {
    if (!this.endDate) return undefined;
    return new LocalizedMoment(this.endDate);
  }

  @computed
  public get duration() {
    if (!this.endDate) return undefined;

    return Math.ceil(
      dayjs.duration(dayjs(this.endDate).diff(this.startDate)).asMinutes()
    );
  }

  @computed
  public get preview() {
    return MarkdownParser.parsePreview(this.description?.text);
  }

  @computed
  public get webinarJoinUrl() {
    const user = this.participants.find((p) => !!p.isCurrentUser);
    return user?.webinarGeekJoinUrl || user?.demioJoinUrl;
  }

  public contains(regex: RegExp) {
    const { name, description } = this;
    const { text = "" } = description || {};

    return regex.test(name) || regex.test(text);
  }
}
